import { useState, useEffect } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  listPasskeys,
  deletePasskey,
  type PasskeyDescriptor,
} from "@/lib/brokerAuth";
import {
  registerPasskey,
  isPasskeySupported,
  describePasskeyError,
} from "@/lib/passkeys";

// Account-security card for passkeys (PMS-S042). A broker can add a passkey
// (platform authenticator / security key) and remove existing ones. The browser
// runs the WebAuthn ceremony; PMS relays it. Hidden gracefully on browsers
// without WebAuthn support.

type Phase =
  | { kind: "loading" }
  | { kind: "ready"; passkeys: PasskeyDescriptor[] };

function formatCreated(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

interface PasskeysCardProps {
  // Whether any MFA factor is active. Passkeys and MFA are mutually exclusive
  // (PMS-S052) — adding a passkey switches MFA off. "Any MFA factor" so SMS folds
  // in here later without changing the pattern.
  mfaActive?: boolean;
  // Called after the passkey set changes, so the page can re-read MFA state
  // (which the server may have just switched off).
  onChange?: () => void;
  // Bumped by the page when MFA is enabled (which deletes passkeys server-side),
  // so this card re-fetches and reflects the removal.
  reloadToken?: number;
  // Surface a "Recommended" badge — set when the broker has no passkey yet, since
  // a passkey is the strongest, phishing-resistant option (BRK-S026).
  recommended?: boolean;
}

export function PasskeysCard({
  mfaActive = false,
  onChange,
  reloadToken = 0,
  recommended = false,
}: PasskeysCardProps) {
  const { authedRequest } = useAuth();
  const supported = isPasskeySupported();
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const { passkeys } = await listPasskeys(authedRequest);
        if (!cancelled) setPhase({ kind: "ready", passkeys });
      } catch {
        // Degrade to an empty, still-actionable list + a note (never a blank card).
        if (!cancelled) {
          setPhase({ kind: "ready", passkeys: [] });
          setError("Couldn’t load your passkeys.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, supported, reloadToken]);

  async function refresh() {
    const { passkeys } = await listPasskeys(authedRequest);
    setPhase({ kind: "ready", passkeys });
  }

  async function onAdd() {
    setError(null);
    setPending(true);
    try {
      await registerPasskey(authedRequest);
      await refresh();
      // The server switches MFA off when a passkey is added (PMS-S052); let the
      // page re-read so the authenticator card reflects it.
      onChange?.();
    } catch (err) {
      // A cancelled ceremony resets quietly; a real failure surfaces inline.
      const message = describePasskeyError(err);
      if (message) setError(message);
    } finally {
      setPending(false);
    }
  }

  async function onRemove(credentialId: string) {
    setError(null);
    setPending(true);
    try {
      await deletePasskey(authedRequest, credentialId);
      await refresh();
      onChange?.();
    } catch (err) {
      const message = describePasskeyError(err);
      if (message) setError(message);
    } finally {
      setPending(false);
    }
  }

  if (!supported) {
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-brk-border bg-brk-panel p-5">
        <h2 className="font-semibold">Passkeys</h2>
        <p className="text-sm text-brk-dim">
          This browser doesn’t support passkeys. Open the portal in a recent
          version of Chrome, Safari, Edge, or Firefox to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold">Passkeys</h2>
          {recommended && (
            <span className="rounded-full bg-brk-accent/10 px-2 py-0.5 text-xs font-medium text-brk-accent">
              Recommended
            </span>
          )}
        </div>
        <p className="text-sm text-brk-dim">
          Sign in with your device’s fingerprint, face, screen lock, or a
          security key — no password or code to type.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      {phase.kind === "loading" && (
        <p role="status" className="text-sm text-brk-dim">
          Loading your passkeys…
        </p>
      )}

      {phase.kind === "ready" && (
        <>
          {phase.passkeys.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {phase.passkeys.map((pk) => {
                const created = formatCreated(pk.createdAt);
                return (
                  <li
                    key={pk.credentialId}
                    className="flex items-center justify-between gap-3 rounded-md border border-brk-border px-3 py-2"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">
                        {pk.friendlyName || "Passkey"}
                      </span>
                      {created && (
                        <span className="text-xs text-brk-dim">
                          Added {created}
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemove(pk.credentialId)}
                      disabled={pending}
                      className="shrink-0 text-sm text-brk-accent hover:underline disabled:opacity-60"
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-brk-dim">
              You haven’t added any passkeys yet.
            </p>
          )}

          {mfaActive && (
            <p className="text-sm text-brk-dim">
              You use a passkey <em>or</em> an authenticator app, not both.
              Adding a passkey will turn off your authenticator app.
            </p>
          )}

          <button
            type="button"
            onClick={onAdd}
            disabled={pending}
            className="self-start rounded-md bg-brk-accent px-4 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:opacity-60"
          >
            {pending ? "Working…" : "Add a passkey"}
          </button>
        </>
      )}
    </div>
  );
}
